import { Department, DepartmentGroups } from '~/models/services/minifd';
import { DEPARTMENT_GROUP_NAMES } from '~/components/in-year-budget-requests/constants/department-group-names';
import { Amount } from '~/components/in-year-budget-requests/constants/amounts';

export const SWEDEN_COUNTRY_CODE = 'NO';
export const HYDRO_TECHNOLOGY_NAME = 'Hydro';
export const HYDRO_TECHNOLOGY_NAMES = ['Hydro', 'Hydro - Germany', 'Hydro - Sweden'] as const;
export const STEAM_TECHNOLOGY_NAME = 'Steam';
export const SWEDEN_HYDRO_ASSET_OPERATIONS_NAME = 'Operations Director';
export const SWEDEN_HYDRO_BUSINESS_CONTROLLING_NAME = 'SVP Business Controlling';

export function isHydroTechnologyLabel(technologyLabel: string | undefined): boolean {
    return HYDRO_TECHNOLOGY_NAMES.some(name => name === technologyLabel);
}

export function isSteamTechnologyLabel(technologyLabel: string | undefined): boolean {
    return technologyLabel === STEAM_TECHNOLOGY_NAME;
}

export function isHydroOrSteamPlant(args: {
    isHydroTechnology: boolean;
    isSteamTechnology: boolean;
}): boolean {
    return args.isHydroTechnology || args.isSteamTechnology;
}

/** Table 3 (no compensation), hydro/steam — Energy Asset (Technology) approver applies. */
export function isHydroOrSteamTable3(args: {
    isTable3Flow: boolean;
    isHydroTechnology: boolean;
    isSteamTechnology: boolean;
}): boolean {
    return args.isTable3Flow && isHydroOrSteamPlant(args);
}

/** Table 3 (no compensation), hydro/steam, total ≤ €200k — four-role workflow with Technology Director/Manager as 3rd. */
export function isHydroOrSteamUnder200kTable3(args: {
    isTable3Flow: boolean;
    totalCost: number;
    isHydroTechnology: boolean;
    isSteamTechnology: boolean;
}): boolean {
    return (
        isHydroOrSteamTable3(args) &&
        args.totalCost <= Amount.TWO_HUNDRED_THOUSAND
    );
}

export type ResolveDepartmentArgs = {
    departmentGroups: DepartmentGroups[];
    groupName: string;
    totalCost: number;
    isTable3Flow: boolean;
    isSwedenCountry: boolean;
    isHydroTechnology: boolean;
    isSteamTechnology?: boolean;
    /** First-year MTP fully covered by compensation (Table 2 path). */
    isCompensated?: boolean;
};

export function loadDepartmentsForGroup(
    departmentGroups: DepartmentGroups[],
    groupName: string,
    isTable3Flow: boolean
): Department[] {
    const approvalGroup = departmentGroups?.find(group => group.name === groupName);
    const approvalGroupDepartments = isTable3Flow
        ? approvalGroup?.budgetOverrunDepartments
        : approvalGroup?.departments;
    return approvalGroupDepartments ?? [];
}

const HEAD_OF_BUSINESS_CONTROLLING_NAMES = new Set([
    'Head of Business Controlling',
    'Head of Local Business Controlling',
]);

/** Departments with real cost bands only (from/to not -1). */
function bandedDepartments(departments: Department[]): Department[] {
    return departments.filter(d => d.from !== -1 && d.to !== -1);
}

/**
 * Picks the role whose configured (from, to] interval contains totalCost (EUR).
 * Skips rows with from/to -1. Out of range: above max band → highest band; below lowest → undefined (caller may resolve Head of BC by name).
 */
export function pickDepartmentByTotalCost(
    departments: Department[],
    totalCost: number
): Department | undefined {
    const banded = bandedDepartments(departments);

    if (banded.length === 0) {
        return undefined;
    }

    const match = banded.find(department =>
        totalCost === 0
            ? totalCost === department.from
            : totalCost > department.from && totalCost <= department.to
    );

    if (match) {
        return match;
    }

    const minFrom = Math.min(...banded.map(d => d.from));
    const maxTo = Math.max(...banded.map(d => d.to));

    if (totalCost > maxTo) {
        return banded.reduce((a, b) => (a.to > b.to ? a : b));
    }

    const hasZeroBand = banded.some(d => d.from === 0);
    if (
        totalCost < minFrom
        || (totalCost === 0 && !hasZeroBand)
    ) {
        return undefined;
    }

    // No matching band (e.g. gap between configured intervals).
    return undefined;
}

const COO_ASSET_OPERATIONS_NAMES = ['COO Asset Operation', 'COO Energy Assets'] as const;
const STEAM_UNDER_TWO_MILLION_NAMES = ['Head of Performance Team'] as const;
const TECHNOLOGY_DIRECTOR_MANAGER_NAMES = ['Technology Director/Manager'] as const;
const TECHNOLOGY_DIRECTOR_NAMES = ['Technology Director'] as const;

function findDepartmentByNames(
    departments: Department[],
    names: readonly string[]
): Department | undefined {
    return names
        .map(n => departments.find(d => d.name === n))
        .find(Boolean);
}

/**
 * Energy Asset (Technology) for plants that are not Sweden + Hydro: role by steam vs other tech and €2M band,
 * then fall back to configured (from, to] bands.
 */
function pickAssetOperationsDepartmentForStandardFlow(
    departments: Department[],
    totalCost: number,
    isHydroTechnology: boolean,
    isSteamTechnology: boolean,
    isCompensated: boolean,
    isTable3Flow: boolean
): Department | undefined {
    const isCooBand = totalCost > Amount.TWO_MILLION && !isCompensated;
    const useTechnologyDirectorOverTwoMillionCompensated =
        isCompensated && totalCost > Amount.TWO_MILLION;
    const useTechnologyDirectorTwoHundredKToTwoMillion =
        !isCompensated &&
        totalCost > Amount.TWO_HUNDRED_THOUSAND &&
        totalCost <= Amount.TWO_MILLION;
    const useTechnologyDirectorUnder200k = isHydroOrSteamUnder200kTable3({
        isTable3Flow,
        totalCost,
        isHydroTechnology,
        isSteamTechnology
    }) && !isCompensated;

    if (isCooBand) {
        const coo = findDepartmentByNames(departments, COO_ASSET_OPERATIONS_NAMES);
        if (coo) {
            return coo;
        }
    } else if (useTechnologyDirectorOverTwoMillionCompensated) {
        const techDirector = findDepartmentByNames(departments, TECHNOLOGY_DIRECTOR_NAMES);
        if (techDirector) {
            return techDirector;
        }
    } else if (useTechnologyDirectorUnder200k) {
        const techDirectorManager = findDepartmentByNames(departments, TECHNOLOGY_DIRECTOR_MANAGER_NAMES);
        if (techDirectorManager) {
            return techDirectorManager;
        }
    } else if (useTechnologyDirectorTwoHundredKToTwoMillion) {
        const techDirector = findDepartmentByNames(departments, TECHNOLOGY_DIRECTOR_NAMES);
        if (techDirector) {
            return techDirector;
        }
    } else if (isSteamTechnology) {
        const headPerformance = findDepartmentByNames(departments, STEAM_UNDER_TWO_MILLION_NAMES);
        if (headPerformance) {
            return headPerformance;
        }
    } else {
        const techDirector = findDepartmentByNames(departments, TECHNOLOGY_DIRECTOR_NAMES);
        if (techDirector) {
            return techDirector;
        }
    }
    return pickDepartmentByTotalCost(departments, totalCost);
}

export function applySwedenHydroDepartmentNameOverride(
    department: Department | undefined,
    groupName: string,
    isSwedenCountry: boolean,
    isHydroTechnology: boolean,
    isTable3Flow = false,
    totalCost = 0,
    isCompensated = false
): Department | undefined {
    const skipSwedenOverride =
        isTable3Flow &&
        !isCompensated &&
        totalCost <= Amount.TWO_HUNDRED_THOUSAND &&
        isHydroTechnology;

    if (!department || !(isSwedenCountry && isHydroTechnology) || skipSwedenOverride) {
        return department;
    }

    if (groupName === DEPARTMENT_GROUP_NAMES.ASSET_OPERATIONS) {
        return { ...department, name: SWEDEN_HYDRO_ASSET_OPERATIONS_NAME };
    }

    if (groupName === DEPARTMENT_GROUP_NAMES.BUSINESS_CONTROLLING) {
        return { ...department, name: SWEDEN_HYDRO_BUSINESS_CONTROLLING_NAME };
    }

    return department;
}

export function resolveDepartmentForGroup({
    departmentGroups,
    groupName,
    totalCost,
    isTable3Flow,
    isSwedenCountry,
    isHydroTechnology,
    isSteamTechnology = false,
    isCompensated = false
}: ResolveDepartmentArgs): Department | undefined {
    const departments = loadDepartmentsForGroup(departmentGroups, groupName, isTable3Flow);
    const tierCost = totalCost;
    // Sweden + hydro uses cost bands only on Table 2; Table 3 needs name-based Technology Director/Manager pick.
    const useSteamRules =
        groupName === DEPARTMENT_GROUP_NAMES.ASSET_OPERATIONS &&
        (!(isSwedenCountry && isHydroTechnology) || isTable3Flow);

    let picked = useSteamRules
        ? pickAssetOperationsDepartmentForStandardFlow(
            departments,
            totalCost,
            isHydroTechnology,
            isSteamTechnology,
            isCompensated,
            isTable3Flow
        )
        : pickDepartmentByTotalCost(departments, tierCost);

    // Table 2 (standard): Head of Local/Business Controlling < €200k. Table 3 (budget overrun): VP BC for same band — do not substitute Head here.
    if (
        !isTable3Flow &&
        groupName === DEPARTMENT_GROUP_NAMES.BUSINESS_CONTROLLING &&
        picked === undefined &&
        tierCost >= 0 &&
        tierCost <= Amount.TWO_HUNDRED_THOUSAND
    ) {
        picked = departments.find(d => HEAD_OF_BUSINESS_CONTROLLING_NAMES.has(d.name));
    }

    return applySwedenHydroDepartmentNameOverride(
        picked,
        groupName,
        isSwedenCountry,
        isHydroTechnology,
        isTable3Flow,
        tierCost,
        isCompensated
    );
}
